import { create } from "apisauce";
import * as Sentry from "@sentry/browser";
import store from "../../store";

export const apiSettings = {
  baseURL: process.env.REACT_APP_API_BASE_URL,
  headers: {
    "Accept": "application/json",
    "Content-Type": "application/json",
  },
  timeout: 60000,
};

const apiErrorMonitor = (response) => {
  // Log all API error status codes except 401 UNAUTHORIZED and null (NETWORK_ERROR)
  if (!response.ok && (!response.status || response.status === 401)) {
    const state = store.getState();
    const currentUser = state.auth.user;

    const errorDetails = {
      ...response,
    };

    Sentry.withScope((scope) => {
      if (currentUser && currentUser.email) {
        scope.setUser({
          id: currentUser.username,
          email: currentUser.email,
          username: currentUser.username,
          Phone: currentUser.phone,
          name: `${currentUser.first_name} ${currentUser.last_name}`,
        });
      }

      scope.setExtra("requestInfo", {
        url: errorDetails.config.url,
        method: errorDetails.config.method,
        headers: errorDetails.config.headers,
        params: errorDetails.config.params,
        response: errorDetails.data,
        responseStatus: errorDetails.status,
      });

      Sentry.captureMessage(
        `${errorDetails.config.url} responded with ${errorDetails.status} status code`
      );
    });
  }
};

const api = create(apiSettings);
const apifile = create({
  ...apiSettings,
  responseType: "arraybuffer",
});

api.addMonitor(apiErrorMonitor);
apifile.addMonitor(apiErrorMonitor);

export const loginBrokeAgentApi = (payload) => {
  return api.post("/v2_0/broker-agent/login/", payload);
};

export const resetPasswordApi = (payload) => {
  return api.post("/v2_0/broker-agent/users/reset-password/username/", payload);
};

export const changePasswordApi = (payload, headers) => {
  return api.post("/v2_0/broker-agent/users/reset-password/", payload, { headers });
};

export const getUserApi = (headers) => {
  return api.get("/v2_0/broker-agent/users/", {}, { headers });
};

export const createLendingRequestApi = (payload, { creditType, locationId }, headers) => {
  return api.post(
    `/create-api-application/${creditType}/${locationId}/`, payload, { headers }
  );
};

export const dismissLendingRequestApi = (payload, { actionType, requestId }, headers) => {
  return api.patch(
    `/v2_0/broker-agent/credit-requests/${actionType}/${requestId}/status/`, payload, { headers }
  );
};

export const getLendingRequestsApi = (filters, headers) => {
  return api.get("/v2_0/broker-agent/credit-requests/", filters, { headers });
};

export const getNomenclaturesApi = (type, headers) => {
  return api.get(`/v2_0/broker-agent/nomenclatures/${type}/`, {}, { headers });
};

export const getApplicationApi = (uuid, headers) => {
  return api.get(
    `/v2_0/application/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getRedirectInstantPaymentApplicationApi = (uuid, headers) => {
  return api.get(
    `/v1_0/payment/enrolment/euplatesc/endpoint/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getApplicationRedirectApi = (uuid, headers) => {
  return api.get(
    `/v2_0/accept-redirect/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const patchApplicationApi = (payload, uuid, headers) => {
  return api.patch(`/v2_0/application/`, payload, {
    headers: {
      ...headers,
      ...(uuid && { "X-User-Online-Key": uuid }),
    },
  });
};

export const getInstallmentsApi = (locationId = "all", uuid, headers) => {
  return api.get(
    `/v2_0/get-products/installments/${locationId}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getLendingRequestApi = (requestId, headers) => {
  return api.get(`/v1_0/credit-requests/${requestId}/`, {}, { headers });
};

export const getLendingRequestDocumentsApi = (requestId, headers) => {
  return api.get(
    `/v1_0/credit-requests/${requestId}/documents/?show_all=true`,
    {},
    { headers }
  );
};

export const getLendingRequestHistoryApi = (requestId, params, headers) => {
  return api.get(
    `/v1_0/credit-requests/${requestId}/history/actions/`,
    { ...params },
    { headers },
  );
};

export const getLendingRequestCommentsApi = (requestId, headers) => {
  return api.get(
    `/v1_0/credit-requests/comments/?credit_request=${requestId}`,
    {},
    { headers }
  );
};

export const getLendingRequestStatusOffersApi = (requestId, headers) => {
  return api.get(
    `/v1_0/credit-requests/${requestId}/comments/destinations/`,
    {},
    { headers }
  );
};

export const postLendingRequestCommentsApi = (
  payload,
  requestId,
  // only in BROKER platform, the offerId is set
  offerId = "",
  headers
) => {
  const suffix = offerId ? `offer/${offerId}/comments` : "comments";

  return api.post(
    `/v1_0/credit-requests/${requestId}/${suffix}/`,
    payload,
    { headers }
  )
};

export const getApplicationDocumentApi = (requestId, headers) => {
  return apifile.get(
    `v1_0/credit-requests/${requestId}/download-application/`,
    {},
    { headers }
  );
};

export const getOfferDocumentApi = (offerId, uuid, payload, headers) => {
  return apifile.post(
    `v2_0/download-contract/${offerId}/`,
    payload,
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getLendingRequestOfferContractApi = (offerId, headers) => {
  return apifile.get(
    `/v1_0/credit-requests/download-contract/${offerId}/`,
    {},
    { headers }
  );
};

export const setLendingRequestOfferStatusApi = (
  statusType,
  offerId,
  headers
) => {
  return api.patch(
    `/v1_0/credit-requests/offer/${statusType}/${offerId}/status/`,
    {},
    { headers }
  );
};

export const setLendingRequestGoodToFinanceStatusApi = (requestId, headers) => {
  return api.patch(
    `/v1_0/credit-requests/good-to-fund/${requestId}/status/`,
    {},
    { headers }
  );
};

export const postLendingRequestOfferContractApi = (payload, headers) => {
  return api.post("/v1_0/credit-requests/upload-contract/", payload, {
    headers,
  });
};

export const ocrApi = (payload, headers) => {
  return api.post("/v1_0/ocr/", payload, { headers });
};

export const getFileApi = (documentType, uuid, headers) => {
  return api.get(
    `/v2_0/document/${documentType}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const postFileApi = (payload, uuid, headers) => {
  return api.post("/v2_0/documents/", payload, {
    headers: {
      ...headers,
      ...(uuid && { "X-User-Online-Key": uuid }),
    },
  });
};

export const postFileBackOfficeApi = (payload, requestId, headers) => {
  return api.post(
    `/v1_0/credit-requests/${requestId}/documents/`,
    payload,
    { headers }
  );
};

export const downloadFileApi = (documentId, uuid, headers) => {
  return apifile.get(
    `/v2_0/download-document/${documentId}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getRegionsApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/region/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getCitiesApi = (regionId, uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/${regionId}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getPostalCodesApi = (localityId, uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/${localityId}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getContactMethodsApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/contact_mode/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getAccountStatementDeliveryModeApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/account_statement_delivery_mode/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    },
  );
};

export const getMarketingContactMethodsApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/marketing_contact_mode/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    },
  );
};

export const postCiApi = (payload, credit_request_id, headers) => {
  return api.patch(
    `/v1_0/credit-requests/${credit_request_id}/phase/personal-data/`,
    payload,
    { headers }
  );
};

export const postClientDetailsApi = (payload, credit_request_id, headers) => {
  return api.patch(
    `/v1_0/credit-requests/${credit_request_id}/phase/assign/`,
    payload,
    { headers }
  );
};

export const postBankAccountApi = (payload, credit_request_id, headers) => {
  return api.patch(
    `/v1_0/credit-requests/${credit_request_id}/phase/bank-account/`,
    payload,
    { headers }
  );
};

export const verifyCbAnafApi = (requestId, headers) => {
  return api.get(
    `/v1_0/credit-requests/${requestId}/bc-anaf/`,
    {},
    { headers }
  );
};

export const getOccupationApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/occupation/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getCivilStatusApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/civil_status/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getEducationApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/education/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getPropertyStatusApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/property_status/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getFundingSourceApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/funding_source/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getEmploymentStatusesApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/parent-nomenclatures/employment/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const getNotificationsApi = (headers) => {
  return api.get("/v1_0/notifications/", {}, { headers });
};

export const markNotificationsApi = (is_read, payload, headers) => {
  return api.patch(
    `/v1_0/notifications/${is_read ? "read" : "unread"}/`,
    payload,
    { headers }
  );
};

export const sendEmailMobileApi = (uuid, headers) => {
  return api.post(
    "/v2_0/notifications/resume-application/",
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const sendEmailNotificationApi = (requestId, headers) => {
  return api.post(
    `/v1_0/notifications/notify/${requestId}/`,
    {},
    { headers }
  );
};

export const getSimulatorOfferEstimatesApi = (data, headers) => {
  const payload = {
    location_id: data.locationId,
    type: data.loanTypeId,
    customer: {
      employment: {
        monthly_income: data.income,
      },
    },
    detail: {
      loan_amount: data.amount,
      installments: data.instalments,
    },
    external_dependency: {
      cb_response: {
        payment_obligations: data.expenses,
      },
    },
  };

  return api.post("/v1_0/credit-requests/simulator/", payload, { headers });
};

export const getScoreApi = (payload, headers) => {
  return api.get(
    `/v1_0/scoring/${payload}/get-score/`,
    {},
    { headers }
  );
};

export const processOffersApi = (payload, headers) => {
  return api.post(`/v1_0/credit-requests/${payload}/process/`, {}, { headers });
};

export const getOffersApi = (payload, headers) => {
  return api.get(`/v1_0/credit-requests/${payload}/offers/`, {}, { headers });
};

export const getEstimatedOffersApi = (payload, headers) => {
  return api.get(
    `/v1_0/credit-requests/${payload}/estimated-offers/`,
    {},
    { headers }
  );
};

export const getActivitiesApi = (payload, uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclature/employment/${payload}/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const esignInitApi = (uuid, headers) => {
  return api.get(
    "/v2_0/esign-connector/initiate-certificate/",
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
      timeout: 240000,
    }
  );
};

export const esignAuthCodeApi = (uuid, headers) => {
  return api.get(
    "/v2_0/esign-connector/initiate-signing/",
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
      timeout: 240000,
    }
  );
};

export const esignResendAuthCodeApi = (uuid, headers) => {
  return api.get(
    "/v2_0/esign-connector/resend-authorization-code/",
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
      timeout: 240000,
    }
  );
};

export const esignPostAuthApi = (uuid, code, headers) => {
  return api.post(
    "/v2_0/esign-connector/authorize-signing/",
    { code },
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
      timeout: 240000,
    }
  );
};

export const renewTokenKyc = (uuid, headers) => {
  return api.get(
    "/v2_0/token/",
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};

export const postCheckValidatedEmailApi = (uuid, payload, headers) => {
  return api.post("v2_0/email/check-validated-email/", payload, {
    headers: {
      ...headers,
      ...(uuid && { "X-User-Online-Key": uuid }),
    },
  });
};

export const postValidateEmailApi = (uuid, payload, headers) => {
  return api.post("v2_0/email/validate-email/", payload, {
    headers: {
      ...headers,
      ...(uuid && { "X-User-Online-Key": uuid }),
    },
  });
};

export const postValidateEmailCodeApi = (uuid, payload, headers) => {
  return api.post("v2_0/email/validate-message/", payload, {
    headers: {
      ...headers,
      ...(uuid && { "X-User-Online-Key": uuid }),
    },
  });
};

export const getAddressTypeApi = (uuid, headers) => {
  return api.get(
    `/v${uuid ? 2 : 1}_0/nomenclatures/address_type/`,
    {},
    {
      headers: {
        ...headers,
        ...(uuid && { "X-User-Online-Key": uuid }),
      },
    }
  );
};


// TRANSACTION
export const getTransactionsApi = (filters, headers) => {
  return api.get(
    "/v1_0/transactions-connector/transaction/",
    filters,
    { headers }
  );
};

export const getTransactionApi = (uuid, headers) => {
  return api.get(
    `/v2_0/transactions-connector/transaction/${uuid}/`,
    {},
    { headers }
  );
};

export const patchTransactionApi = (uuid, payload, headers) => {
  return api.patch(
    `/v2_0/transactions-connector/transaction/${uuid}/`,
    payload,
    { headers }
  );
};

export const getTransactionInstallmentsApi = (uuid, headers) => {
  return api.get(
    `/v2_0/transactions-connector/config/installments/${uuid}/`,
    {},
    { headers }
  );
};

export const getTransactionValidateEmailApi = (uuid, headers) => {
  return api.get(
    `v2_0/transactions-connector/email/is-validated/${uuid}/`,
    {},
    { headers }
  );
};

export const getTransactionEmailCodeApi = (uuid, headers) => {
  return api.get(
    `v2_0/transactions-connector/email/send-code/${uuid}/`,
    {},
    { headers }
  );
};

export const postTransactionEmailCodeApi = (uuid, payload, headers) => {
  return api.post(
    `v2_0/transactions-connector/email/validate-code/${uuid}/`,
    payload,
    { headers }
  );
};

export const getTransactionSmsCodeApi = (uuid, headers) => {
  return api.get(
    `v2_0/transactions-connector/sms/send-code/${uuid}/`,
    {},
    { headers }
  );
};

export const postTransactionSmsCodeApi = (uuid, payload, headers) => {
  return api.post(
    `v2_0/transactions-connector/sms/validate-code/${uuid}/`,
    payload,
    { headers }
  );
};

export const getTransactionInfoApi = (uuid, headers) => {
  return api.get(
    `/v1_0/transactions-connector/getinfo/${uuid}/`,
    {},
    { headers }
  );
};

export const getTransactionRedirectInstantPaymentApi = (uuid, headers) => {
  return api.get(
    `/v1_0/transactions-connector/payment/enrolment/euplatesc/${uuid}/endpoint/`,
    {},
    { headers }
  );
};

export const getTransactionDocumentsApi = (uuid, headers) => {
  return api.get(
    `v2_0/docs-connector/all/${uuid}/`,
    {},
    { headers }
  );
};

export const getTransactionContractApi = (uuid, headers) => {
  return apifile.get(
    `v2_0/docs-connector/pdf/transaction_debit/${uuid}/?stream=1`,
    {},
    { headers }
  );
};

export const postTransactionAssignUserApi = (uuid, payload, headers) => {
  return api.post(
    `/v1_0/transactions-connector/action/${uuid}/assign/`,
    payload,
    { headers }
  );
};

export const cancelTransactionApi = (uuid, loggedIn, headers) => {
  return api.post(
    `v${loggedIn ? 1 : 2}_0/transactions-connector/action/${uuid}/cancel/`,
    {},
    { headers }
  );
};

export const postApproveTransactionApi = (uuid, payload, headers) => {
  return api.post(
    `/v1_0/transactions-connector/bo-action/${uuid}/done/`,
    payload,
    { headers }
  );
};

export const sendEmailTransactionApi = (uuid, headers) => {
  return api.post(
    `/v1_0/transactions-connector/notify-tx-link/${uuid}/`,
    {},
    { headers }
  );
};
